import {
  Center,
  getColorByPath,
  getTextColorByPath,
  Hide,
  Icon,
  Image,
  Integer,
  Show,
  ThemeColorPath,
  ThemeColorTextPath,
  ThemeIconKey,
} from 'platform/foundation';
import {cn, cva} from 'platform/shared';
import {match} from 'ts-pattern';

import {always, isNil, isNotEmpty} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useComponentsTheme} from '../../hooks/useComponentsTheme';
import {getInitials} from './getInitials';

type AvatarSize = 'xSmall' | 'small' | 'default' | 'large';
type AvatarVariant = 'square' | 'circle';

export interface AvatarProps extends TestIdProps {
  size?: AvatarSize;
  variant?: AvatarVariant;
  color?: ThemeColorPath;
  textColor?: ThemeColorTextPath;
  icon?: ThemeIconKey;
  iconColor?: ThemeColorPath;
  name?: string | null;
  imageUrl?: string | null;
}

const avatarVariants = cva('', {
  variants: {
    size: {
      xSmall: 'size-6',
      small: 'size-7',
      default: 'size-10',
      large: 'size-[4.5rem]',
    },
    variant: {
      square: 'rounded-[var(--radius-md)]',
      circle: 'rounded-[var(--radius-full)]',
    },
  },
  defaultVariants: {
    size: 'default',
    variant: 'circle',
  },
});

const avatarImageVariants = cva('[&>div>div]:overflow-hidden', {
  variants: {
    variant: {
      square: '[&>div>div]:rounded-[var(--radius-md)]',
      circle: '[&>div>div]:rounded-[var(--radius-full)]',
    },
  },
  defaultVariants: {
    variant: 'circle',
  },
});

const avatarTextVariants = cva(
  'whitespace-nowrap [font-family:var(--font-family-body)] font-[var(--font-weight-medium)] leading-none',
  {
    variants: {
      size: {
        xSmall: 'text-[length:0.625rem]',
        small: 'text-[length:var(--text-size-x-small)]',
        default: 'text-[length:1rem]',
        large: 'text-[length:1.75rem]',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

export function Avatar(props: AvatarProps) {
  const theme = useComponentsTheme();
  const showImage = isNotNil(props.imageUrl) && isNotEmpty(props.imageUrl);
  const showName = isNotNil(props.name) && isNotEmpty(props.name) && !showImage;

  const size = match<AvatarSize, Integer>(props.size ?? 'default')
    .with('xSmall', always(6))
    .with('small', always(7))
    .with('default', always(10))
    .with('large', always(18))
    .exhaustive();

  const iconSize = match<AvatarSize, Integer>(props.size ?? 'default')
    .with('xSmall', always(3))
    .with('small', always(4))
    .with('default', always(6))
    .with('large', always(10))
    .exhaustive();

  const borderRadius = props.variant === 'square' ? 'medium' : 'circular';

  const backgroundColor = isNil(props.imageUrl)
    ? props.color || DEFAULT_BACKGROUND_COLOR
    : DEFAULT_BACKGROUND_COLOR;

  return (
    <div
      className={cn(
        avatarVariants({size: props.size, variant: props.variant}),
        showImage ? avatarImageVariants({variant: props.variant}) : undefined
      )}
      style={{backgroundColor: getColorByPath(backgroundColor, theme)}}
      data-testid={suffixTestId('avatar', props)}
    >
      <Center width={size} height={size}>
        <Show when={showImage}>
          <Image
            src={props.imageUrl}
            height="100%"
            width="100%"
            isLazy
            hasSpinner
            borderRadius={borderRadius}
          />
        </Show>

        <Show when={showName}>
          <div
            className={avatarTextVariants({size: props.size})}
            style={{
              color: getTextColorByPath(props.textColor || DEFAULT_TEXT_COLOR, theme),
            }}
          >
            {getInitials(props.name ?? '')}
          </div>
        </Show>

        <Hide when={showName || showImage}>
          <Icon value={props.icon ?? 'social/person'} size={iconSize} color={props.iconColor} />
        </Hide>
      </Center>
    </div>
  );
}

const DEFAULT_TEXT_COLOR = 'primary';
const DEFAULT_BACKGROUND_COLOR = 'palettes.neutral.30.100';
